import { useMemo, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import useSWR from "swr";

import { TicketDetail } from "./Tickets";
import { syncedAgo } from "../utils/syncedAgo";

import {
  api,
  type Me,
  type SlaPosture,
  type SlaShow,
  type SlaSummary,
  type TicketScope,
} from "../services/api";

const IQUEST_STAFF_ROLES: ReadonlyArray<string> = ["service_desk", "iquest_admin"];
const COMPANY_VIEW_PERMISSION = "view_all_company_tickets";

type PriorityToken = "" | "low" | "medium" | "high" | "critical";

const PRIORITY_OPTIONS: { value: PriorityToken; label: string }[] = [
  { value: "", label: "Any priority" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const POSTURE_LABEL: Record<SlaPosture, string> = {
  breached: "Breached",
  at_risk: "At risk",
  on_track: "On track",
  // Not "No SLA", which read as "we owe you nothing here" on a ticket we had
  // already answered (issue #94).
  responded: "Responded",
  no_target: "Response target not recorded",
};

const SHOW_OPTIONS: { value: SlaShow; label: string }[] = [
  { value: "awaiting", label: "Awaiting first response" },
  { value: "responded", label: "Already responded" },
  { value: "all", label: "All open tickets" },
  // The percentage only covers answered tickets with a recorded target. A
  // missing target does not establish that no SLA applies (Refs #111).
  { value: "answered_window", label: "Answered with targets, last 30 days" },
];

/** What the SLA column says for one row, or null when this build cannot name
 *  the posture the server sent.
 *
 * An answered ticket reports the VERDICT, not merely that it was answered:
 * "Responded" leaves a customer unable to tell a breach from a save, which is
 * the one thing this page exists to tell them. Tickets still awaiting an answer
 * report their live posture.
 */
function slaCell(row: {
  posture: SlaPosture;
  sla_met: boolean | null;
}): { label: string; cls: string } | null {
  if (row.posture === "responded") {
    if (row.sla_met === true) {
      return { label: "SLA met", cls: "badge badge-progress" };
    }
    if (row.sla_met === false) {
      return { label: "SLA missed", cls: "badge badge-critical" };
    }
    // A missing recorded target prevents assessment, not a contractual SLA.
    return { label: "Response target not recorded", cls: "badge badge-closed" };
  }
  // Typed as SlaPosture, but the value comes off the wire: a browser holding a
  // build older than the API (which is what staging serves, and what any client
  // sitting on a cached bundle serves) receives a posture this map has no entry
  // for. The lookup then handed a badge an undefined label and the column
  // rendered as an empty grey pill, reading as a broken page rather than an
  // unknown value (issue #106). Annotated, because the index signature will not
  // admit the miss on its own.
  const label: string | undefined = POSTURE_LABEL[row.posture];
  if (!label) return null;
  return { label, cls: postureBadgeClass(row.posture) };
}

function postureBadgeClass(posture: SlaPosture): string {
  switch (posture) {
    case "breached":
      return "badge badge-critical";
    case "at_risk":
      return "badge badge-waiting";
    case "on_track":
      return "badge badge-progress";
    case "responded":
      return "badge badge-progress";
    default:
      return "badge badge-closed";
  }
}

function rowClass(row: { posture: SlaPosture; sla_met: boolean | null }): string {
  // A ticket answered late carries the same accent as a live breach: the SLA was
  // missed either way, and the row should not read as settled-and-fine.
  if (row.posture === "breached") return "sla-row sla-row-breached";
  if (row.posture === "responded" && row.sla_met === false) {
    return "sla-row sla-row-breached";
  }
  if (row.posture === "at_risk") return "sla-row sla-row-at-risk";
  return "sla-row";
}

function fmtDeadline(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

function availableScopes(me: Me | undefined): TicketScope[] {
  if (!me) return ["my"];
  const isStaff = IQUEST_STAFF_ROLES.includes(me.role);
  const isAdmin = me.role === "client_admin";
  const hasCompanyView =
    isStaff || isAdmin || (me.permissions ?? []).includes(COMPANY_VIEW_PERMISSION);
  const scopes: TicketScope[] = ["my"];
  if (hasCompanyView) scopes.push("company");
  if (isStaff) scopes.push("all");
  return scopes;
}

function defaultScope(me: Me | undefined): TicketScope {
  if (!me) return "my";
  if (IQUEST_STAFF_ROLES.includes(me.role)) return "all";
  if (me.role === "client_admin") return "company";
  if ((me.permissions ?? []).includes(COMPANY_VIEW_PERMISSION)) return "company";
  return "my";
}

function scopeLabel(scope: TicketScope): string {
  if (scope === "my") return "My tickets";
  if (scope === "company") return "All company tickets";
  return "All tickets";
}

function metTone(percent: number | null): string {
  if (percent === null) return "muted";
  if (percent >= 95) return "success";
  if (percent >= 85) return "warning";
  return "danger";
}

export function SLA() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const scopes = useMemo(() => availableScopes(me), [me]);
  const [scope, setScope] = useState<TicketScope | null>(null);
  const activeScope: TicketScope = scope ?? defaultScope(me);
  const [priority, setPriority] = useState<PriorityToken>("");
  // Which tickets the table lists. The stat cards always describe every open
  // ticket, so this never changes a number above the table.
  //
  // Opens on the answered history rather than the outstanding queue: the first
  // question anyone brings to this page is "did we miss any?", and that view
  // lists the misses first. What is still awaiting a response is right there in
  // the cards above, and one click away in the filter.
  //
  // The SERVER still defaults to "awaiting" when no view is given. That is the
  // cheaper query and the right thing to fall back to for a caller that did not
  // ask; this page always asks explicitly.
  const [show, setShow] = useState<SlaShow>("answered_window");
  // The ticket opened over this list. Working down a list of breached tickets is
  // the whole point of the page, and navigating away to read one cost several
  // clicks to get back (issue #100, the same complaint as #91).
  const [openTicket, setOpenTicket] = useState<string | null>(null);

  const key = me ? ["/api/sla/summary", activeScope, priority, show] : null;
  const { data, isLoading, error, isValidating, mutate } = useSWR<SlaSummary>(
    key,
    () =>
      api.sla.summary({
        scope: activeScope,
        priority: priority || undefined,
        show,
      }),
    { refreshInterval: 300000 },
  );

  /** Open the ticket over the list, leaving the browser's own "open elsewhere"
   *  gestures alone so cmd/ctrl/middle-click still gets a real new tab. */
  function openInPlace(
    e: ReactMouseEvent<HTMLAnchorElement>,
    ticketNumber: string,
  ) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    setOpenTicket(ticketNumber);
  }

  const showCompany = activeScope === "all";
  const counts = data?.counts;
  const showLabel =
    SHOW_OPTIONS.find((o) => o.value === show)?.label ?? "Open tickets";

  return (
    <>
      {/* Named for the contract it reports against. "Posture" was iQuest's own
          word and is gone (#99), but "Service Level Agreement" is the term
          clients hold us to and expect to see (issue #105). */}
      <div className="page-header">
        <h1 className="page-title">Service Level Agreement</h1>
        <p className="page-subtitle">
          How your open tickets are tracking against the time we commit to
          responding in, and how we have done over the last
          {data ? ` ${data.met_window_days}` : " 30"} days.
        </p>
      </div>

      {scopes.length > 1 && (
        <div className="scope-tabs" role="tablist">
          {scopes.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={activeScope === s}
              className={`scope-tab ${activeScope === s ? "active" : ""}`}
              onClick={() => {
                setScope(s);
                setPriority("");
              }}
            >
              {scopeLabel(s)}
            </button>
          ))}
        </div>
      )}

      {/* The three cards below count tickets AWAITING a first response, and the
          table defaults to exactly those, so the cards add up to the rows. They
          used to count three postures while the table listed every open ticket,
          which read as 0/0/0 above a full table (issue #94). */}
      <h2 className="sla-section-title">Awaiting first response</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Breached</div>
          <div className="stat-value danger">
            {error ? "—" : counts ? counts.breached : "…"}
          </div>
          <div className="stat-subtext">Past the time we committed to</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">At risk</div>
          <div className="stat-value warning">
            {error ? "—" : counts ? counts.at_risk : "…"}
          </div>
          {/* No mention of business hours or the warning thresholds: the SLA's
              own timings are contractual and the customer already has them, so
              restating them here is noise at best and a second source of truth
              at worst (issue #104). */}
          <div className="stat-subtext">Response due soon</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">On track</div>
          <div className="stat-value success">
            {error ? "—" : counts ? counts.on_track : "…"}
          </div>
          {/* Not "comfortably": the system knows the deadline is further out
              than the warning window, and nothing more than that. */}
          <div className="stat-subtext">Inside the response time</div>
        </div>
        <button
          type="button"
          className="stat-card sla-card-button"
          onClick={() => setShow("answered_window")}
          title={
            "Tickets with a recorded response target first answered in the last " +
            `${data?.met_window_days ?? 30} days, counted on the day they were ` +
            "answered. The service review reports a calendar month, counted on " +
            "the day each ticket was raised, so the two figures answer " +
            "different questions and will not match. Click to list them."
          }
        >
          <div className="stat-label">
            Answered on time, last {data?.met_window_days ?? 30} days
          </div>
          <div className={`stat-value ${metTone(data?.sla_met_percent ?? null)}`}>
            {error
              ? "—"
              : data
                ? data.sla_met_percent === null
                  ? "—"
                  : `${data.sla_met_percent}%`
                : "…"}
          </div>
          {data && data.sla_total_count > 0 ? (
            <>
              <div className="stat-subtext">
                {data.sla_met_count} of {data.sla_total_count} answered in time
              </div>
              <div className="stat-subtext">
                Only tickets with a recorded response target
              </div>
            </>
          ) : data ? (
            <div className="stat-subtext">
              No answered tickets with a recorded response target
            </div>
          ) : null}
        </button>
      </div>

      {/* These figures are computed from the ticket mirror, which lags Autotask
          by up to a sync interval. A ticket answered since the last sync still
          counts as breached above, so the page says how old the reading is
          rather than letting staleness read as a wrong number (issue #98). */}
      {data && (
        <p className="sla-freshness">
          Figures as at the last ticket sync
          {data.synced_at ? <> · {syncedAgo(data.synced_at)}</> : <> · never synced</>}
        </p>
      )}

      <div className="ticket-filter-row">
        <select
          aria-label="Which tickets to list"
          value={show}
          onChange={(e) => setShow(e.target.value as SlaShow)}
        >
          {SHOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as PriorityToken)}
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => void mutate()}
          disabled={isValidating}
        >
          {isValidating ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                {showCompany && <th>Company</th>}
                <th>Priority</th>
                <th>Status</th>
                <th>SLA</th>
                <th>First response due</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className="ticket-empty" colSpan={showCompany ? 6 : 5}>
                    Loading response times…
                  </td>
                </tr>
              )}
              {error && !isLoading && (
                <tr>
                  <td className="ticket-empty" colSpan={showCompany ? 6 : 5}>
                    {String((error as Error).message ?? error)}
                  </td>
                </tr>
              )}
              {!isLoading && !error && data && data.tickets.length === 0 && (
                <tr>
                  <td className="ticket-empty" colSpan={showCompany ? 6 : 5}>
                    {show === "awaiting"
                      ? "Nothing is awaiting a first response."
                      : show === "responded"
                        ? "No open tickets have been responded to yet."
                        : show === "answered_window"
                          ? "No answered tickets with a recorded response target in this window."
                          : "No open tickets in scope."}
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                data &&
                data.tickets.map((t, i) => (
                  <tr
                    key={t.ticketNumber ?? t.id ?? `row-${i}`}
                    className={rowClass(t)}
                  >
                    <td>
                      {t.ticketNumber ? (
                        <a
                          href={`/tickets/${t.ticketNumber}`}
                          onClick={(e) => openInPlace(e, t.ticketNumber as string)}
                          title="Open this ticket here"
                        >
                          {t.title ?? "(untitled)"}
                        </a>
                      ) : (
                        <span>{t.title ?? "(untitled)"}</span>
                      )}
                      <div className="row-meta">{t.ticketNumber ?? ""}</div>
                    </td>
                    {showCompany && <td>{t.company_name ?? "—"}</td>}
                    <td>{t.priority_label ?? "—"}</td>
                    <td>
                      <span className="badge badge-open">
                        {t.status_label ?? "—"}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        const cell = slaCell(t);
                        // The same dash the other unknown cells use, rather
                        // than a pill with nothing in it.
                        return cell ? (
                          <span className={cell.cls}>{cell.label}</span>
                        ) : (
                          "—"
                        );
                      })()}
                    </td>
                    <td>{fmtDeadline(t.first_response_due)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {data && data.tickets.length > 0 && (
          <div className="sla-table-note">
            {/* The answered-window view is a different population from the open
                tickets the cards count, so it is described in its own terms
                rather than "of N open". */}
            <span>
              {show === "answered_window" ? (
                <>
                  {showLabel}: {data.tickets.length}
                  {data.tickets.length === 1 ? " ticket" : " tickets"}
                  {data.sla_total_count > data.sla_met_count && (
                    <>
                      {" · "}
                      {data.sla_total_count - data.sla_met_count} answered late,
                      listed first
                    </>
                  )}
                </>
              ) : (
                <>
                  {showLabel}: {data.tickets.length} of {data.total_open} open
                  {data.total_open === 1 ? " ticket" : " tickets"}
                </>
              )}
            </span>
            {/* Say it rather than just stopping: a truncated list otherwise
                reads as the end of the tickets. The counts above are complete
                either way. */}
            {data.tickets_truncated && (
              <span>
                Showing the first {data.tickets.length}. Narrow by priority to
                see the rest.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Portalled to the body so it stacks above the page rather than inside
          the table's stacking context. Escape closes the ticket only: the modal
          listens on document in the capture phase and stops the event. */}
      {openTicket &&
        createPortal(
          <TicketDetail
            ticketNumber={openTicket}
            onClose={() => {
              setOpenTicket(null);
              // A reply or status change from inside the modal can move the
              // ticket's posture, so re-read the summary on close.
              void mutate();
            }}
          />,
          document.body,
        )}
    </>
  );
}
